import { useStore } from '../store';

/**
 * Shows whether the current annotation pass is Round 1 or Round 2.
 *
 * Only meaningful for tenants whose required_votes is 2 (two independent
 * annotators per episode) and only while actually annotating — not when
 * reviewing/auditing. The round is derived from how many *other* operators
 * have already voted on this episode when it was opened:
 *   0 prior votes -> this operator is Round 1
 *   1 prior vote  -> this operator is Round 2
 *
 * The prelabel bot (model_operator) votes as an independent first voter on
 * secshot flows, so it is excluded from the prior-vote count — otherwise a
 * human first annotator would wrongly read as Round 2.
 */
export function RoundIndicator() {
  const viewerMode = useStore((s) => s.viewerMode);
  const currentEpisodeVotes = useStore((s) => s.currentEpisodeVotes);
  const operator = useStore((s) => s.operator);
  const statsCache = useStore((s) => s.statsCache);
  const myStatsCache = useStore((s) => s.myStatsCache);

  if (viewerMode !== 'annotating') return null;

  const requiredVotes = myStatsCache?.required_votes ?? statsCache?.required_votes ?? 2;
  if (requiredVotes !== 2) return null;

  const modelOperator = statsCache?.model_operator ?? myStatsCache?.model_operator ?? null;

  const priorOperators = new Set<string>();
  (currentEpisodeVotes ?? []).forEach((vote) => {
    const name = vote?.operator;
    if (!name || name === operator || name === modelOperator) return;
    priorOperators.add(name);
  });

  const round = Math.min(requiredVotes, priorOperators.size + 1);

  return (
    <span className="rounded-full border border-brand-orange/40 bg-brand-orange/10 px-2.5 py-1 text-xs font-semibold text-brand-orange whitespace-nowrap">
      Round {round} / {requiredVotes}
    </span>
  );
}
